import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  createShapeMtn,
  deleteShapeMtn,
  updateShapeMtn,
} from '@/services/graphql/blueprint'
import { SectionSchema, Shape } from '@/types'

const client = getAmplifyClient()

export type ShapeWithLegacyParent = Shape & { shapeSubShapesId?: string }

type ShapeCreate = {
  upsertType: 'create' | 'update'
  input: ShapeWithLegacyParent
}

type ShapeDelete = {
  upsertType: 'delete'
  input: { id: string; sectionShapesId?: string }
}

export type ShapeParams = ShapeCreate | ShapeDelete

export const normalizeShapeParent = (input: ShapeWithLegacyParent): Shape => {
  const { shapeSubShapesId, ...shape } = input
  const parentShapesId = shape.parentShapesId ?? shapeSubShapesId

  return {
    ...shape,
    ...(parentShapesId ? { parentShapesId } : {}),
  }
}

export const useUpsertShape = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createShape'],
    mutationFn: upsertShape,
    onMutate: ({ upsertType, input }) => {
      queryClient.cancelQueries({
        queryKey: ['section', input.sectionShapesId],
      })
      queryClient.setQueryData(
        ['section', input.sectionShapesId],
        (data: SectionSchema) => {
          const initialData = data || { id: input.sectionShapesId, shapes: [] }
          const shapes = initialData.shapes?.items || []
          if (upsertType === 'delete') {
            const newShapes = shapes.filter((shape) => shape.id !== input.id)
            return { ...initialData, shapes: { items: newShapes } }
          }
          const shapeInput = normalizeShapeParent(input)
          const newShapes =
            upsertType === 'create'
              ? [...shapes, shapeInput]
              : shapes.map((shape) =>
                  shape.id === input.id ? shapeInput : shape,
                )
          return { ...initialData, shapes: { items: newShapes } }
        },
      )
    },
  })
}

const fieldsToRemove = [
  'createdAt',
  'updatedAt',
  'children',
  'parentId',
  'treeLevel',
  'shapeSubShapesId',
]

export const prepareShapeMutationInput = (input: ShapeWithLegacyParent) => {
  const shape = { ...normalizeShapeParent(input) }
  fieldsToRemove.forEach((field) => delete shape[field])
  return shape
}

const upsertShape = async ({ upsertType, input }: ShapeParams) => {
  switch (upsertType) {
    case 'create':
      return client.graphql({
        query: createShapeMtn,
        variables: { input: prepareShapeMutationInput(input) },
      }) as Promise<GraphQLResult<{ createShape: { createdAt: string } }>>
    case 'update':
      return client.graphql({
        query: updateShapeMtn,
        variables: { input: prepareShapeMutationInput(input) },
      }) as Promise<GraphQLResult<{ updateShape: { updatedAt: string } }>>
    case 'delete':
      return client.graphql({
        query: deleteShapeMtn,
        variables: { input: { id: input.id } },
      }) as Promise<GraphQLResult<{ deleteShape: { updatedAt: string } }>>
  }
}
